import {
  ComponentStyleProps,
  componentStylePropsOf,
} from "../../../../lib/styles/props/component-style-props";
import React, { useCallback, useMemo } from "react";
import {
  Box,
  ComponentMultiStyleConfig,
  HStack,
  Link,
  Text,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import { DeleteIconButton } from "../../../icon-button";
import { CMDatetimeText } from "../../../text";
import { hasValue } from "../../../../lib/util/common-util";
import { FileUploadItemContent } from "./types";

export type CMItemFileUploadProps = {
  editMode?: "editable" | "disabled" | "readOnly";
  variant?: "default" | "error" | "diff";

  /**
   * アップロード済みファイルの1レコード分の情報
   */
  fileItem: FileUploadItemContent;

  /**
   * 変更モードで新規追加されたファイルに表示するメッセージ
   */
  messageAddition?: string;

  /**
   * 変更モードで削除されたファイルに表示するメッセージ
   */
  messageDelete?: string;

  /**
   * ボタン押下時、呼び出し元に削除を通知
   */
  onDelete?: (fileId: string) => void;
} & ComponentStyleProps;

/**
 * 添付ファイルを表示するコンポーネント
 */
export const CMItemFileUpload = ({
  editMode = "editable",
  variant = "default",

  fileItem,

  messageAddition,
  messageDelete,

  onDelete,

  sx,
  ...rest
}: CMItemFileUploadProps) => {
  const styles = useMultiStyleConfig("CMItemFileUploadStyles");

  const disabled = editMode === "disabled" || editMode === "readOnly";

  const isDeletedInDiffMode = useMemo(
    () => hasValue(fileItem.isDeleted) && fileItem.isDeleted,
    [fileItem.isDeleted],
  );
  const isAddedInDiffMode = useMemo(
    () => hasValue(fileItem.isAdded) && fileItem.isAdded,
    [fileItem.isAdded],
  );

  /**
   * 表示モードの設定
   */
  const displayMode = useMemo<"add" | "delete" | "none">(() => {
    // 変更モード以外の場合はスタイル設定を行わない
    if (variant !== "diff") {
      return "none";
    }

    if (hasValue(fileItem.isAdded) && fileItem.isAdded) return "add";

    if (hasValue(fileItem.isDeleted) && fileItem.isDeleted) return "delete";

    return "none";
  }, [fileItem, variant]);

  /**
   * 変更モードで表示するラベルの設定
   */
  const stateLabel = useMemo<string>(() => {
    switch (displayMode) {
      case "add":
        return messageAddition ?? "";
      case "delete":
        return messageDelete ?? "";
      case "none":
        return "";
    }
  }, [displayMode, messageDelete, messageAddition]);

  const appliedFileStyles = useMemo(
    () => ({
      ...styles.fileBase,
      ...(displayMode === "add" ? styles.fileAddition : undefined),
      ...(displayMode === "delete" ? styles.fileDeleted : undefined),
    }),
    [styles, displayMode],
  );

  const handleDelete = useCallback(() => {
    onDelete?.(fileItem.id);
  }, [onDelete, fileItem]);

  return (
    <HStack sx={{ ...styles.container, ...sx }} {...rest}>
      <Link href={fileItem.url} isExternal sx={appliedFileStyles}>
        {fileItem.name}
      </Link>
      <CMDatetimeText value={fileItem.date} sx={styles.date} />

      {!disabled && !isDeletedInDiffMode && (
        <Box hidden={fileItem.isDeleted} height={"20px"}>
          <DeleteIconButton
            tipMessage={fileItem.deleteButtonTip}
            ariaLabel="delete-button"
            onClick={handleDelete}
            sx={styles.deleteButton}
          />
        </Box>
      )}
      {(isDeletedInDiffMode || isAddedInDiffMode) && (
        <Text sx={styles.stateLabel}>{stateLabel}</Text>
      )}
    </HStack>
  );
};

export const CMItemFileUploadStyles: ComponentMultiStyleConfig = {
  parts: [
    "container",
    "fileBase",
    "fileAddition",
    "fileDeleted",
    "date",
    "deleteButton",
    "stateLabel",
  ],
  baseStyle: {
    container: {},
    fileBase: {
      fontWeight: "400",
      fontSize: "14px",
      lineHeight: "16px",
      color: "blue.600",
      px: "3px",
      py: "1px",
      maxW: "470px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    fileAddition: {
      borderWidth: "1px",
      borderColor: "orange.500",
    },
    fileDeleted: {
      color: "gray.400",
      backgroundColor: "gray.600",
    },
    date: {
      fontWeight: "400",
      fontSize: "12px",
      lineHeight: "16px",
      color: "gray.500",
    },
    deleteButton: {
      borderRadius: "20px",
      h: "20px",
      w: "20px",
      minW: "20px",
      top: "-5px",

      fontSize: "12px",
      color: "gray.400",

      _hover: {
        bgColor: "gray.100",
        color: "gray.600",
      },
    },

    stateLabel: {
      fontWeight: "400",
      fontSize: "12px",
      lineHeight: "16px",
      color: "gray.900",
    },
  },
};
